import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Stack,
  Collapse,
  IconButton,
  Grid,
  LinearProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { fetchStatus, StatusData, Deployment, DeploymentComponent, Incident, DailyUptime } from '@/services/statusApi';

const STATUS_COLORS = {
  operational: 'success',
  degraded: 'warning',
  outage: 'error',
  unknown: 'default',
} as const;

const STATUS_ICONS = {
  operational: <CheckCircleIcon />,
  degraded: <WarningIcon />,
  outage: <ErrorIcon />,
  unknown: <WarningIcon />,
};

const STATUS_LABELS = {
  operational: 'All Systems Operational',
  degraded: 'Partial System Outage',
  outage: 'Major System Outage',
  unknown: 'Status Unknown',
};

function StatusBadge({ status }: { status: string }) {
  const color = STATUS_COLORS[status as keyof typeof STATUS_COLORS] || 'default';
  const icon = STATUS_ICONS[status as keyof typeof STATUS_ICONS];

  return (
    <Chip
      icon={icon}
      label={status.charAt(0).toUpperCase() + status.slice(1)}
      color={color as 'success' | 'warning' | 'error' | 'default'}
      size="small"
    />
  );
}

// Uptime history visualization - shows 90 days as colored bars
function UptimeHistoryBar({ history, uptime90d }: { history?: DailyUptime[]; uptime90d?: number }) {
  // Take last 90 days and reverse so oldest is first
  const days = (history || []).slice(0, 90).reverse();

  const getUptimeColor = (uptime: number) => {
    if (uptime >= 99.9) return 'success.main';
    if (uptime >= 99) return 'success.light';
    if (uptime >= 95) return 'warning.main';
    if (uptime >= 90) return 'warning.light';
    return 'error.main';
  };

  const uptimeColor = uptime90d !== undefined
    ? (uptime90d >= 99.9 ? 'success' : uptime90d >= 99 ? 'warning' : 'error')
    : 'default';

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography variant="caption" color="text.secondary">
          90-day uptime
        </Typography>
        {uptime90d !== undefined && (
          <Typography variant="caption" fontWeight={600} color={`${uptimeColor}.main`}>
            {uptime90d.toFixed(2)}%
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: '1px',
          height: 24,
          borderRadius: 0.5,
          overflow: 'hidden',
          bgcolor: 'grey.200',
        }}
      >
        {days.length > 0 ? (
          days.map((day, index) => (
            <Box
              key={day.date || index}
              sx={{
                flex: 1,
                minWidth: 2,
                bgcolor: day.total_checks > 0 ? getUptimeColor(day.uptime) : 'grey.300',
                '&:hover': {
                  opacity: 0.8,
                },
                cursor: 'default',
              }}
              title={`${day.date}: ${day.uptime.toFixed(1)}% uptime (${day.operational_checks}/${day.total_checks} checks)`}
            />
          ))
        ) : (
          <Box sx={{ flex: 1, bgcolor: 'grey.300' }} title="No data available" />
        )}
      </Box>
      <Box display="flex" justifyContent="space-between" mt={0.5}>
        <Typography variant="caption" color="text.secondary">
          90 days ago
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Today
        </Typography>
      </Box>
    </Box>
  );
}

function DeploymentCard({ deployment }: { deployment: Deployment }) {
  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box>
          <Typography variant="h6" component="a"
            href={`https://${deployment.domain}`}
            target="_blank"
            rel="noopener"
            sx={{ textDecoration: 'none', color: 'inherit', '&:hover': { textDecoration: 'underline' } }}
          >
            {deployment.domain}
          </Typography>
          {deployment.environment && (
            <Chip
              label={deployment.environment}
              size="small"
              variant="outlined"
              sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
        <StatusBadge status={deployment.status} />
      </Box>

      {/* Uptime History */}
      <Box mt={2} mb={2}>
        <UptimeHistoryBar history={deployment.uptime_history} uptime90d={deployment.uptime_90d} />
      </Box>

      {deployment.components && deployment.components.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Components
          </Typography>
          <List dense>
            {deployment.components.map((component: DeploymentComponent, index: number) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemText
                  primary={component.name}
                  secondary={component.latency_ms ? `${component.latency_ms}ms` : null}
                />
                <StatusBadge status={component.status} />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Paper>
  );
}

const INCIDENT_STATUS_COLORS = {
  investigating: 'error',
  identified: 'warning',
  monitoring: 'info',
  resolved: 'success',
} as const;

const SEVERITY_COLORS = {
  minor: 'warning',
  major: 'error',
  critical: 'error',
} as const;

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}

function IncidentCard({ incident }: { incident: Incident }) {
  const [expanded, setExpanded] = useState(incident.status !== 'resolved');
  const severityColor = SEVERITY_COLORS[incident.severity as keyof typeof SEVERITY_COLORS] || 'default';
  const statusColor = INCIDENT_STATUS_COLORS[incident.status as keyof typeof INCIDENT_STATUS_COLORS] || 'default';

  return (
    <Card
      sx={{
        mb: 2,
        borderLeft: 4,
        borderColor: incident.severity === 'critical' ? 'error.main' :
                     incident.severity === 'major' ? 'error.light' :
                     'warning.main',
      }}
    >
      <CardContent sx={{ pb: expanded ? 1 : '16px !important' }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography variant="subtitle1" fontWeight={600}>
                {incident.title}
              </Typography>
              <Chip
                label={incident.severity}
                size="small"
                color={severityColor as 'warning' | 'error' | 'default'}
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Chip
                label={incident.status.replace('_', ' ')}
                size="small"
                color={statusColor as 'error' | 'warning' | 'info' | 'success' | 'default'}
                variant="outlined"
                sx={{ height: 22 }}
              />
              <Box display="flex" alignItems="center" gap={0.5}>
                <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {formatRelativeTime(incident.created_at)}
                </Typography>
              </Box>
              {incident.affected_components && incident.affected_components.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Affecting: {incident.affected_components.join(', ')}
                </Typography>
              )}
            </Stack>
          </Box>
          {incident.updates && incident.updates.length > 0 && (
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>

        <Collapse in={expanded}>
          {incident.updates && incident.updates.length > 0 && (
            <Box mt={2}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Updates
              </Typography>
              <List dense sx={{ mt: 0.5 }}>
                {incident.updates.map((update, index) => (
                  <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={update.status}
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {formatRelativeTime(update.timestamp)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {update.message}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
}

function UptimeBar({ uptime }: { uptime: number }) {
  const color = uptime >= 99.9 ? 'success' : uptime >= 99 ? 'warning' : 'error';
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography variant="body2" color="text.secondary">
          90-day uptime
        </Typography>
        <Typography variant="body2" fontWeight={600} color={`${color}.main`}>
          {uptime.toFixed(2)}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={uptime}
        color={color}
        sx={{ height: 8, borderRadius: 1 }}
      />
    </Box>
  );
}

export default function StatusPage() {
  const [status, setStatus] = useState<StatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const data = await fetchStatus();
        setStatus(data);
        setError(null);
      } catch (err) {
        setError('Failed to load status data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadStatus();

    // Refresh every 30 seconds
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const overallStatus = status?.overall_status || 'unknown';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 3,
        }}
      >
        <Container maxWidth="md">
          <Box display="flex" alignItems="center" gap={{ xs: 2, sm: 3 }}>
            <img
              src="/roboticks-icon.svg"
              alt="Roboticks"
              style={{ height: 40, width: 'auto', flexShrink: 0 }}
            />
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Roboticks Status
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Real-time status of all Roboticks services
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Overall Status */}
        <Paper
          sx={{
            p: 3,
            mb: 4,
            bgcolor:
              overallStatus === 'operational'
                ? 'success.50'
                : overallStatus === 'degraded'
                  ? 'warning.50'
                  : 'error.50',
            border: 1,
            borderColor:
              overallStatus === 'operational'
                ? 'success.200'
                : overallStatus === 'degraded'
                  ? 'warning.200'
                  : 'error.200',
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                color:
                  overallStatus === 'operational'
                    ? 'success.main'
                    : overallStatus === 'degraded'
                      ? 'warning.main'
                      : 'error.main',
              }}
            >
              {STATUS_ICONS[overallStatus as keyof typeof STATUS_ICONS]}
            </Box>
            <Typography variant="h5" fontWeight={600}>
              {STATUS_LABELS[overallStatus as keyof typeof STATUS_LABELS]}
            </Typography>
          </Box>
          {status?.checked_at && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Last updated: {new Date(status.checked_at).toLocaleString()}
            </Typography>
          )}
        </Paper>

        {/* Active Incidents */}
        {status?.active_incidents && status.active_incidents.length > 0 && (
          <Box mb={4}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <ErrorIcon color="error" />
              <Typography variant="h6" fontWeight={600}>
                Active Incidents
              </Typography>
              <Chip
                label={status.active_incidents.length}
                size="small"
                color="error"
              />
            </Box>
            <Divider sx={{ mb: 2 }} />
            {status.active_incidents.map((incident) => (
              <IncidentCard key={incident.incident_id} incident={incident} />
            ))}
          </Box>
        )}

        {/* Uptime Overview */}
        {status?.uptime_90d !== undefined && (
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Uptime
            </Typography>
            <UptimeBar uptime={status.uptime_90d} />
          </Paper>
        )}

        {/* Services */}
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Services
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {status?.deployments && status.deployments.length > 0 ? (
          <Grid container spacing={2}>
            {status.deployments.map((deployment) => (
              <Grid item xs={12} md={6} key={deployment.deployment_id}>
                <DeploymentCard deployment={deployment} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography color="text.secondary">No deployments found.</Typography>
        )}

        {/* Recent Incidents */}
        {status?.recent_incidents && status.recent_incidents.length > 0 && (
          <Box mt={4}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Recent Incidents
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {status.recent_incidents.slice(0, 5).map((incident) => (
              <IncidentCard key={incident.incident_id} incident={incident} />
            ))}
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Powered by Roboticks
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
